import { Component, OnInit, OnDestroy, ElementRef, ViewChild } from "@angular/core";
import { DatePipe } from "@angular/common";
import { Router } from "@angular/router";

import { GridOptions, ColDef, GridApi } from "ag-grid-community";

import { DateService } from "../../common/services/date.service";
import { LoginAuditSuccessInclusion, ReportsService } from "../reports.service";
import { SubHeaderService } from "../../core/services/sub-header.service";

import { LoginAuditRecord } from "../model/login-audit-record";
import { SubHeader } from "../../core/models/sub-header.model";
import { GridFormatterService } from "../../common/services/grid-formatter.service";
import { DynamicDialogService } from "../../core/services/dynamic-dialog.service";

@Component({
    selector: "app-login-auth",
    templateUrl: "./login-auth.component.html",
    styleUrls: ["./login-auth.component.scss"],
    standalone: false
})

export class LoginAuthComponent implements OnInit {

    excelStyles = this.formatService.excelStyles;

    data: LoginAuditRecord[];
	gridOptions: GridOptions;
	gridApi?: GridApi;
    currentDate: Date = new Date();
    toDate?: Date | null;
    fromDate?: Date | null;
    futureDateLimit?: Date | null;
    pastDateLimit?: Date | null;

    subHeader = new SubHeader({
        title: "Login Audits Report",
        buttons: [{ name: "Return to Dashboard", method: () => this.goHome() }]
    });

    successFilter: LoginAuditSuccessInclusion = LoginAuditSuccessInclusion.Both;
    placeholder: string = DateService.DateFormat;

    constructor(
        public readonly router: Router,
        private readonly dateService: DateService,
        private readonly formatService: GridFormatterService,
        public readonly subheaderService: SubHeaderService,
        public readonly reportsService: ReportsService,
        public readonly datePipe: DatePipe,
        private readonly dialog: DynamicDialogService
	) { }

    ngOnInit() {
        this.subheaderService.updateSubHeader(this.subHeader);
        this.fromDateChange(this.dateService.generateDateWithOffset(0, -1, 0));
        this.toDateChange(this.currentDate);
        let coldefs = this.createColumnDefinitions();
        this.gridOptions = this.formatService.BuildDefaultGridOptionsWithColumns(coldefs, false, true, true, false);
        this.gridOptions.overlayLoadingTemplate = '<span class="ag-overlay-loading-center">Please wait ...</span>';
		this.gridOptions.onGridReady = (params) => { 
            this.gridApi = params.api; 
            this.gridApi.sizeColumnsToFit();
        };
        this.search();
    }

    blankFieldFormatter(component: any, field: string) {
        return obj => {
            return obj.data[field] && obj.data[field].length > 0 ? obj.data[field] : "Unknown";
        };
    }

    createColumnDefinitions(): any {
        return [
            {
                headerName: "Attempted Login Date & Time",
                headerTooltip: "Attempted Login Date & Time",
                tooltipValueGetter: this.formatService.formatDate("attemptedTimestamp", "MM/dd/yyyy hh:mm:ss a", "N/A"),
                field: "attemptedTimestamp",
                valueFormatter: this.formatService.formatDate("attemptedTimestamp", "MM/dd/yyyy hh:mm:ss a", "N/A"),
                cellClass: "dateTimeType"
            } as ColDef,
            {
                headerName: "Username",
                headerTooltip: "Username",
                tooltipField: "username",
                field: "username"
            } as ColDef,
            {
                headerName: "IP Address",
                headerTooltip: "IP Address",
                tooltipField: "ipAddress",
                field: "ipAddress"
            } as ColDef,
            {
                headerName: "Successful Login",
                headerTooltip: "Successful Login",
                tooltipValueGetter: obj => (obj.data["success"] ? "yes" : "no"),
                field: "success",
                valueGetter: obj => (obj.data["success"] ? "yes" : "no"),
            } as ColDef,
            {
                headerName: "Reason",
                headerTooltip: "Reason",
                tooltipField: "reason",
                field: "reason"
            } as ColDef
        ];
    }

    goHome(): void {
        this.router.navigate(["home"]);
    }

    search(): void {
        if (this.gridApi) {
            this.gridApi.setGridOption("loading", false);
        }

        this.reportsService.getLoginReport(this.fromDate, this.toDate, this.successFilter).subscribe({
            next: (result) => {
                this.data = result;
                if (this.gridApi) {
                    this.gridApi.setGridOption("rowData", result ?? []);
                    this.gridApi.setGridOption("loading", false);
                }
            },
            error: (error) => {
                console.error(error);
                if (this.gridApi) this.gridApi.setGridOption("rowData", []);
                this.dialog.showError();
            }
        });
    }

    downloadReport() {        
        const fileName = `LoginAuditReport_${this.datePipe.transform(this.fromDate, 'yyyy-MM-dd')}_${this.datePipe.transform(this.toDate, 'yyyy-MM-dd')}`;
        const sheetName = "Login Audit Report";
        this.formatService.exportDataToExcel(fileName, sheetName, this.gridApi);
    }

    get filterDisabled(): string {
        return this.fromDate == null || this.toDate == null ? 'disabled' : null;
    }

    fromDateChange(evt: Date | null): void {
        this.fromDate = evt;
        if (this.fromDate != null) {
            this.pastDateLimit = this.fromDate;
            this.pastDateLimit.setDate(this.fromDate.getDate() + 1);
        } else {
            this.pastDateLimit = null;
        }
    }

    toDateChange(evt: Date | null): void {
        this.toDate = evt;
        if (this.toDate != null) {
            this.futureDateLimit = this.toDate;
            this.futureDateLimit.setDate(this.toDate.getDate() - 1);
        } else {
            this.futureDateLimit = null;
        }
    }

}
